// src/components/AlertsPanel.tsx (v2.7)
// 設計図 §4b: 要確認診療科 6 カテゴリ × TOP6 = 36 件のスポットライト
//   + 「総合 要対策 (4 指標合成スコア)」を上部に追加
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useFilters } from "../store";
import { fmtByUnit, fmtRatio, ymJp } from "../lib/format";
import type { AlertItem, AlertsResponse } from "@shared/api";

const CATEGORIES: { key: keyof Omit<AlertsResponse, "sqlMs">; title: string; subtitle: string; metric: string; unit: string }[] = [
  { key: "budgetUnder",    title: "入院収入予算比 未達", subtitle: "対象月の 実績÷予算 (率の低い順)",   metric: "入院収入",     unit: "千円" },
  { key: "yoyDown",        title: "入院収入前年比 低下", subtitle: "対象月の 実績÷前年 (率の低い順)",   metric: "入院収入",     unit: "千円" },
  { key: "volDown",        title: "入院患者延数 減少",   subtitle: "対象月の入院患者延数 前年比 (低い順)", metric: "入院患者延数", unit: "人日" },
  { key: "priceDown",      title: "1日入院単価 低下",    subtitle: "対象月の入院単価 前年比 (低い順)",   metric: "入院単価",     unit: "円/日" },
  { key: "newPatientDown", title: "新規入院数 減少", subtitle: "対象月の新規入院数 前年比 (低い順)", metric: "新規入院数",   unit: "件" },
  { key: "stayUp",         title: "平均在院日数 長期化", subtitle: "対象月の平均在院日数 前年比 (高い順)", metric: "平均在院日数", unit: "日" },
];

export function AlertsPanel() {
  const f = useFilters();
  const { data, isLoading } = useQuery<AlertsResponse>({
    queryKey: ["alerts", f.granularity, f.iryo, f.yotei, f.asOfMonth],
    queryFn: () => api.alerts({
      granularity: f.granularity, iryo: f.iryo, yotei: f.yotei, asOfMonth: f.asOfMonth,
    }),
    placeholderData: (prev) => prev,
  });

  // 総合スコア計算: 各 dept について 4 カテゴリで「悪化 rank (1=最悪)」を集計
  // → 複数 cat で常連の dept ほど高 score → 「総合 要対策」
  const overallTop = useMemo(() => {
    if (!data) return [];
    type Score = { 診療科: string; cats: string[]; totalDiff: number };
    const m = new Map<string, Score>();
    const allCats: { items: AlertItem[]; label: string }[] = [
      { items: data.budgetUnder,    label: "入院収入予算比未達" },
      { items: data.yoyDown,        label: "入院収入前年比低下" },
      { items: data.volDown,        label: "入院患者延数減少" },
      { items: data.priceDown,      label: "1日入院単価低下" },
      { items: data.newPatientDown, label: "新規入院数減少" },
      { items: data.stayUp,         label: "平均在院日数長期化" },
    ];
    for (const c of allCats) {
      c.items.forEach((x) => {
        if (!m.has(x.診療科)) m.set(x.診療科, { 診療科: x.診療科, cats: [], totalDiff: 0 });
        const s = m.get(x.診療科)!;
        s.cats.push(c.label);
        // 全 cat の悪化額を加算 (千円換算で比較するため割合に変換)
        if (c.label === "入院収入予算比未達" || c.label === "入院収入前年比低下") {
          s.totalDiff += -Math.abs(x.diff);
        } else if (c.label === "平均在院日数長期化") {
          // ratio > 1 が悪化 → (1 - ratio) * 1000 で score 減算
          s.totalDiff += (1 - (x.ratio ?? 1)) * 1000;
        } else if (x.ratio != null) {
          // 1日入院単価低下/入院患者延数減少は ratio で normalize
          s.totalDiff += (x.ratio - 1) * 1000;  // -10% → -100
        }
      });
    }
    // 2 カテゴリ以上に含まれてる dept を TOP に
    // sort 順: cats.length 多い順 (1st)、 totalDiff 小さい順 (2nd、 悪化大きい)
    return Array.from(m.values())
      .filter((s) => s.cats.length >= 2)
      .sort((a, b) => (b.cats.length - a.cats.length) || (a.totalDiff - b.totalDiff))
      .slice(0, 6);
  }, [data]);

  return (
    <section className="card p-4">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-base font-semibold">⚠ 要確認診療科 (対象月 {ymJp(f.asOfMonth)})</h2>
        {data && <span className="perf-pill">SQL {data.sqlMs} ms</span>}
        {isLoading && <span className="text-xs text-slate-400">loading…</span>}
      </div>

      {overallTop.length > 0 && (
        <div className="mb-3 bg-red-50 border-l-4 border-red-400 rounded p-3">
          <div className="text-xs font-semibold text-red-900 mb-2">
            🚨 総合 要対策 診療科 (複数カテゴリで悪化)
            <span className="ml-2 text-[10px] font-normal text-slate-600">
              ・🔥 = 4カテゴリ以上で悪化 ・⚠ = 2-3カテゴリ
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {overallTop.map((s, i) => {
              const severity = s.cats.length >= 4 ? "🔥" : "⚠";
              const sevColor = s.cats.length >= 4 ? "border-red-500 bg-red-50" : "border-amber-300 bg-amber-50/40";
              return (
                <button
                  key={s.診療科}
                  onClick={() => f.setFocusedDept(s.診療科)}
                  className={`border-2 rounded p-2 text-left hover:bg-red-50 ${sevColor}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-800">
                      <span className="mr-1">{severity}</span>{i + 1}. {s.診療科}
                    </span>
                    <span className="text-xs text-slate-600 font-semibold" title="該当した要対策カテゴリ数">{s.cats.length}/6 項目</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-1">{s.cats.join(" / ")}</div>
                  <div className="text-[10px] text-blue-700 hover:underline mt-1">→ 深掘り</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        {CATEGORIES.map((c) => (
          <AlertList
            key={c.key}
            title={c.title}
            subtitle={c.subtitle}
            unit={c.unit}
            items={data?.[c.key] ?? []}
            onSelectDept={(dept) => f.setFocusedDept(dept)}
          />
        ))}
      </div>
      <div className="text-[10px] text-slate-400 mt-2">
        💡 診療科名クリックでフォーカス (KPI / トレンド / 集計表 が連動)
      </div>
    </section>
  );
}

function AlertList({ title, subtitle, unit, items, onSelectDept }: {
  title: string; subtitle: string; unit: string; items: AlertItem[];
  onSelectDept: (d: string) => void;
}) {
  // 比較基準(予算/前年)を title から導出し、 各行の率に明示する(スクロールで見出しが外れても基準が分かる)。
  const basis: "予算" | "前年" = title.includes("予算") ? "予算" : "前年";
  return (
    <div className="border border-slate-200 rounded p-3 bg-slate-50/50">
      <div className="text-xs text-slate-500 font-semibold">{title}</div>
      <div className="text-[10px] text-slate-400 mb-2">{subtitle}</div>
      {items.length === 0 ? (
        <div className="text-xs text-slate-400">該当なし</div>
      ) : (
        <ol className="space-y-1.5">
          {items.map((x, i) => (
            <AlertItemRow
              key={i} item={x} rank={i + 1} unit={unit} basis={basis}
              onSelectDept={onSelectDept}
            />
          ))}
        </ol>
      )}
    </div>
  );
}

function AlertItemRow({ item, rank, unit, basis, onSelectDept }: {
  item: AlertItem; rank: number; unit: string; basis: "予算" | "前年";
  onSelectDept: (d: string) => void;
}) {
  const dept = item["診療科"];
  return (
    <li className="text-xs">
      <button
        onClick={() => onSelectDept(dept)}
        className="w-full text-left hover:bg-white/80 rounded px-1 py-0.5 cursor-pointer"
        title="クリックでこの診療科にフォーカス"
      >
        <div className="flex items-center justify-between">
          <span className="text-slate-700 font-medium underline decoration-dotted decoration-slate-300">
            {rank}. {dept}
          </span>
          <span className="text-red-700 font-semibold" title={`対 ${basis}`}>
            <span className="text-[9px] text-slate-400 font-normal mr-0.5">{basis === "予算" ? "予" : "前"}</span>
            {item.ratio != null ? fmtRatio(item.ratio) : "—"}
          </span>
        </div>
        <div className="text-[10px] text-slate-500">
          差 {fmtByUnit(item.diff, unit)} (実 {fmtByUnit(item.value, unit)} vs 比 {fmtByUnit(item.comparator, unit)})
        </div>
      </button>
    </li>
  );
}
